import { useState } from 'react';
import {
  Stack,
  Text,
  Textarea,
  Button,
  Group,
  ActionIcon,
  Tabs,
  TextInput,
} from '@mantine/core';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { RichTextEditor } from './RichTextEditor';
import { ProductTranslations, ProductContent } from '@/types';

interface TranslationsEditorProps {
  translations: ProductTranslations;
  onChange: (translations: ProductTranslations) => void;
}

const LANGUAGES = [
  { code: 'pl', label: 'Polish (PL)' },
  { code: 'en', label: 'English (EN)' },
  { code: 'de', label: 'German (DE)' },
  { code: 'fr', label: 'French (FR)' },
  { code: 'es', label: 'Spanish (ES)' },
  { code: 'it', label: 'Italian (IT)' },
];

export function TranslationsEditor({ translations, onChange }: TranslationsEditorProps) {
  const [activeTab, setActiveTab] = useState<string>('pl');

  const updateTranslation = (lang: string, field: keyof ProductContent, value: string | string[]) => {
    const newTranslations = { ...translations };
    if (!newTranslations[lang]) {
      newTranslations[lang] = {
        shortDescription: '',
        description: '',
        applications: [],
        documents: [],
        tests: [],
      };
    }
    newTranslations[lang] = {
      ...newTranslations[lang],
      [field]: value,
    };
    onChange(newTranslations);
  };

  const addListItem = (lang: string, field: 'applications' | 'documents' | 'tests') => {
    const current = translations[lang]?.[field] || [];
    updateTranslation(lang, field, [...current, '']);
  };

  const updateListItem = (lang: string, field: 'applications' | 'documents' | 'tests', index: number, value: string) => {
    const current = translations[lang]?.[field] || [];
    const updated = [...current];
    updated[index] = value;
    updateTranslation(lang, field, updated);
  };

  const removeListItem = (lang: string, field: 'applications' | 'documents' | 'tests', index: number) => {
    const current = translations[lang]?.[field] || [];
    updateTranslation(lang, field, current.filter((_, i) => i !== index));
  };

  const currentLang = translations[activeTab] || {
    shortDescription: '',
    description: '',
    applications: [],
    documents: [],
    tests: [],
  };

  return (
    <Stack gap="md">
      <div>
        <Text size="sm" fw={500} mb="xs">
          Translations
        </Text>
        <Text size="xs" c="dimmed">
          Add translations for different languages. Polish and English are required.
        </Text>
      </div>

      <Tabs value={activeTab} onChange={(value) => setActiveTab(value || 'pl')}>
        <Tabs.List>
          {LANGUAGES.map((lang) => (
            <Tabs.Tab key={lang.code} value={lang.code}>
              {lang.label}
            </Tabs.Tab>
          ))}
        </Tabs.List>

        {LANGUAGES.map((lang) => (
          <Tabs.Panel key={lang.code} value={lang.code} pt="md">
            <Stack gap="md">
              <Textarea
                label="Short Description"
                placeholder="Brief product description"
                value={translations[lang.code]?.shortDescription || ''}
                onChange={(e) => updateTranslation(lang.code, 'shortDescription', e.target.value)}
                rows={3}
              />

              <RichTextEditor
                label="Description"
                content={translations[lang.code]?.description || ''}
                onChange={(content) => updateTranslation(lang.code, 'description', content)}
              />

              {/* Applications */}
              <div>
                <Group justify="space-between" mb="xs">
                  <Text size="sm" fw={500}>Applications</Text>
                  <Button
                    leftSection={<IconPlus size={16} />}
                    variant="light"
                    size="xs"
                    onClick={() => addListItem(lang.code, 'applications')}
                  >
                    Add
                  </Button>
                </Group>
                <Stack gap="xs">
                  {(currentLang.applications || []).map((app, index) => (
                    <Group key={index} gap="xs">
                      <TextInput
                        placeholder="Application use case"
                        value={app}
                        onChange={(e) => updateListItem(lang.code, 'applications', index, e.target.value)}
                        style={{ flex: 1 }}
                      />
                      <ActionIcon
                        color="red"
                        variant="subtle"
                        onClick={() => removeListItem(lang.code, 'applications', index)}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>
                  ))}
                </Stack>
              </div>

              {/* Documents */}
              <div>
                <Group justify="space-between" mb="xs">
                  <Text size="sm" fw={500}>Documents</Text>
                  <Button
                    leftSection={<IconPlus size={16} />}
                    variant="light"
                    size="xs"
                    onClick={() => addListItem(lang.code, 'documents')}
                  >
                    Add
                  </Button>
                </Group>
                <Stack gap="xs">
                  {(currentLang.documents || []).map((doc, index) => (
                    <Group key={index} gap="xs">
                      <TextInput
                        placeholder="Document name"
                        value={doc}
                        onChange={(e) => updateListItem(lang.code, 'documents', index, e.target.value)}
                        style={{ flex: 1 }}
                      />
                      <ActionIcon
                        color="red"
                        variant="subtle"
                        onClick={() => removeListItem(lang.code, 'documents', index)}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>
                  ))}
                </Stack>
              </div>

              {/* Tests */}
              <div>
                <Group justify="space-between" mb="xs">
                  <Text size="sm" fw={500}>Tests</Text>
                  <Button
                    leftSection={<IconPlus size={16} />}
                    variant="light"
                    size="xs"
                    onClick={() => addListItem(lang.code, 'tests')}
                  >
                    Add
                  </Button>
                </Group>
                <Stack gap="xs">
                  {(currentLang.tests || []).map((test, index) => (
                    <Group key={index} gap="xs">
                      <TextInput
                        placeholder="Test description"
                        value={test}
                        onChange={(e) => updateListItem(lang.code, 'tests', index, e.target.value)}
                        style={{ flex: 1 }}
                      />
                      <ActionIcon
                        color="red"
                        variant="subtle"
                        onClick={() => removeListItem(lang.code, 'tests', index)}
                      >
                        <IconTrash size={16} />
                      </ActionIcon>
                    </Group>
                  ))}
                </Stack>
              </div>
            </Stack>
          </Tabs.Panel>
        ))}
      </Tabs>
    </Stack>
  );
}

